import React, { useState, useRef } from 'react'
import {
	AppBar, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress,
	Stack, Tab, Tabs, Typography
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from 'react-draggable'
import stylesheet from '../detail-container.stylesheet'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import DialogTitleHeaderComponent from './dialog-title-header.component';
import ConfirmationDialogComponent from './confirmation-dialog.component'
// KB display names carry sorting prefixes (ros:, i.cv.tte:, lab.kd:, fam.hx:).
// They organise the data and are ugly on screen; formatDisplayName strips them.
import { formatDisplayName } from '../../../../utils/display-name-formatter'

interface IProps {
	open: boolean
	handleSubmit: any
	handleClose: any
	input: any
	classes: ClassNameMap
}

interface StyledTabsProps {
	value: number
	onChange: (event: React.ChangeEvent<{}>, newValue: number) => void
	variant: "scrollable" | "standard" | "fullWidth" | undefined
}
type compSet = { title: string; index: number; comp: string; props: any }

interface TabPanelProps {
	children?: React.ReactNode
	index: any
	value: any
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props
	return (<div
		role="tabpanel"
		hidden={value !== index}
		id={`simple-tabpanel-${index}`}
		aria-labelledby={`simple-tab-${index}`}
		{...other}
	>
		{value === index && <>{children}</>}
	</div>
	)
}

const ConditionEvaluationDialogComponent: React.FC<IProps> = ({ open, handleSubmit, handleClose, input, classes }) => {
	const [loading, setLoading] = useState(false)
	const lifestyleRef = useRef(null) as any
	const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
	const [configItem] = useState({
		focusTabIndex: 0,
		tabs: [
			{
				title: input.stage === 'screening' ? 'Recommendation' : 'Evaluation',
				index: 0,
				comp: '',
				props: {}
			},
		]
	})
	const [value, setValue] = useState(configItem?.focusTabIndex || 0 as number)
	const handleChange = (event: any, newValue: React.SetStateAction<number>) => setValue(newValue)

	const [confirmOpen, setConfirmOpen] = useState(false)

	const handleConfirmClose = () => setConfirmOpen(false)

	const confirmStatus = () => {
		handleEvalSubmit('Rejected')
		setConfirmOpen(false)
	}

	const a11yProps = (index: number) => {
		return {
			id: `scrollable-auto-tab-${index}`,
			'aria-controls': `scrollable-auto-tabpanel-${index}`,
		}
	}

	const getPriority = (val: any, isProbability = false) => {
		return <Typography color="textSecondary" variant="body2" className={classes.evalBoxLabel}>
			{val ? isProbability ? `${val}%` : val : '-'
			}
		</Typography>
	}

	const handleEvalSubmit = (type = '') => {
		setLoading(true)
		handleSubmit(type)
		setLoading(false)
	}

	const getVal = (title: any, val: any) => {
		return <Stack direction={'row'} spacing={0.8}>
			<Typography className={classes.evalBoxLabel}>
				<strong>{title}</strong>
			</Typography>
			<Typography className={classes.evalBoxLabel} variant="body2">
				{val}
			</Typography>
		</Stack>
	}

	const isDisabled = () => {
		return input.stage === 'confirmatory testing'
			? !input?.data?.some((i: any) => i?.cond_approval_status === null)
			: false
	}

	const renderCondition = () => {
		return <>
			{input?.data?.map((i: any) => {
				return (i.selected_for_evaluation &&
					(input.stage === 'confirmatory testing' && i.cond_approval_status !== 'Approved')
				) &&
					<Stack direction="column" className={classes.evalToxCard} mb={1}>
						<Card sx={{ padding: '0.5rem 0.5rem 0rem 0.8rem !important', backgroundColor: 'var(--color-eval-card-bg)' }}>
							<CardContent sx={{ padding: '0rem 0rem 0.5rem 0rem !important' }}>
								{getVal('Name', i.condition_name)}
								{getVal('Priority', getPriority(i.priority, false))}
								{getVal('Probability', getPriority(i.probability, true))}
								{(input.stage === 'confirmatory testing') &&
									getVal('Condition approval status', i?.cond_approval_status || '')
								}
								{renderRecommendation('Rationale', i?.current_rationale || {})}
							</CardContent>
						</Card>
					</Stack>
			})}
		</>
	}

	const getLabel = (val: any) => <Typography className={classes.evalBoxLabel}>
		<strong>{val}</strong>
	</Typography>

	const getCriteriaList = (criteriaObj: any) => {
		const cList = criteriaObj && Object.keys(criteriaObj)?.map((m: any, key: any) => {
			const { value, dependents, dependent_has_override } = criteriaObj[m]

			const highlightCriteria = (dKey: string) => {
				return <Typography component={'span'} className={classes.evalBoxLabel} variant="body2"
					color={dependent_has_override
						? dKey === null ? 'var(--color-eval-highlight-purple)' : 'var(--color-eval-highlight-blue)'
						: 'inherit'}
				>
					{dKey === null ? JSON.stringify(dKey) : dKey}
				</Typography>
			}

			const depenList = dependents && Object.keys(dependents)?.map((key: any, index: any) =>
				<Typography component={'span'} className={classes.evalBoxLabel} variant="body2">
					<Typography component={'span'} className={classes.evalBoxLabel} variant="body2">
						{formatDisplayName(key)} {dependents && ` : `}
					</Typography>
					{highlightCriteria(dependents[key])}
					{(index + 1) < Object.keys(dependents).length &&
						<Typography component={'span'} className={classes.evalBoxLabel} variant="body2" color="textSecondary">{`,`}&nbsp;</Typography>
					}
				</Typography>
			)

			return <Grid item key={`${key}-${m}}`}>
				<Stack direction={'row'} sx={{ width: 'max-content', backgroundColor: dependent_has_override ? 'var(--color-eval-highlight-bg)' : 'inherit' }}>
					<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">
						{formatDisplayName(m)}
					</Typography>
					<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">:&nbsp;</Typography>
					<Typography className={classes.evalBoxLabel} variant="body2"
						color={'textSecondary'}>
						{JSON.stringify(value)}
					</Typography>
					{depenList?.length > 0 &&
						<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">
							&nbsp;[{depenList}]
						</Typography>
					}
				</Stack>
			</Grid>
		})

		return <Grid item className={classes.fullWidth}>
			<Grid container direction="column">
				<Grid item>
					<Grid container direction="column" className={classes.evalSubBox}>
						{cList}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	}

	const renderRecommendation = (title: any, currData: any) => {
		return <Grid container direction="column">
			{(input.stage === 'screening') &&
				<Grid item>
					<Stack direction={'row'} spacing={0.5}>
						{getLabel('Recommended to move to confirmatory testing stage')}
						<Typography component={'span'} className={classes.evalBoxLabel} variant="body2">
							{JSON.stringify(currData.recommend_screening_to_diagnosis_progression)}
						</Typography>
					</Stack>
				</Grid>
			}
			<Grid item>{getLabel('Rationale')}</Grid>
			<Grid item className={classes.fullWidth}>
				<Grid container direction="column">
					<Grid item className={classes.fullWidth}>
						{currData &&
							getCriteriaList(input.stage === 'screening' ? currData?.rationale : currData)
						}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	}

	return <>
		{open &&
			<Draggable ref={lifestyleRef} defaultPosition={{ x: 80, y: 0 }} handle='.tox-dialog'>
				<Dialog
					id={`tox-${input?.id}`}
					fullWidth={true}
					maxWidth="md"
					open={open}
					onClose={handleClose}
					hideBackdrop
					disableScrollLock
					sx={{
						position: "absolute",
						left: '14rem',
						top: '10rem'
					}}
					onKeyUp={(e) => {
						e.preventDefault();
						if (e.key === 'Enter' && !isDisabled()) handleEvalSubmit('Approved')
					}}
				>
					<DialogTitle sx={{ padding: '0.5rem 0.5rem 0rem 1rem !important' }} classes={{ root: 'tox-dialog' }}>
						<DialogTitleHeaderComponent title={input?.title} handleDialogClose={handleClose} />
					</DialogTitle>
					<DialogContent className={classes.dialogContentStyle}>
						<Box mb={1}>
							<div className={classes.root}>
								<AppBar position="static" color="transparent">
									<Tabs value={value} onChange={handleChange} variant="standard">
										{configItem?.tabs?.map((c: any) => (<Tab
											key={`config-${c.title}`}
											label={c.title}
											{...a11yProps(c.index)}
											disabled={false}
										/>
										))}
									</Tabs>
								</AppBar>
								<Grid className={classes.linearProgressContainer}>
									{loading === true && <LinearProgress />}
									<Box className={classes.dialogHeaderBox} mb={1}>
										{/* <div className={classes.dialogHeader} /> */}
										{configItem?.tabs?.map((c: any, i: any) => (
											<Box pt={1.5} pl={2} pr={2} key={`compset-${i}`} >
												<TabPanel value={value} index={c.index}>
													{i === 0 && (
														input.stage === 'screening'
															? renderRecommendation('Rationale', input?.data || {})
															: renderCondition()
													)}
												</TabPanel>
											</Box>
										))}
									</Box>
								</Grid>
							</div>
						</Box>
					</DialogContent>
					<DialogActions className={`${classes.btnContainer} ${classes.boxBorder}`}>
						<Grid container spacing={2} justifyContent="right" className={classes.containerPadding}>
							<Grid item>
								<Button
									disabled={loading || isDisabled()}
									variant="outlined"
									size='small'
									className={classes.diagnosisApproveBtn}
									onClick={() => handleEvalSubmit('Approved')}
								>
									<strong>{input?.buttonLabel || `Approve`}</strong>
								</Button>
							</Grid>
							<Grid item>
								<Button
									disabled={loading}
									variant="outlined"
									size='small'
									className={classes.diagnosisApproveBtn}
									onClick={() => setConfirmOpen(true)}
								>
									<strong>{`Exit without diagnosis`}</strong>
								</Button>
							</Grid>
							<Grid item>
								<Button
									disabled={loading}
									className={classes.diagnosisApproveBtn}
									onClick={handleClose}
									variant="outlined"
									size='small'
								>
									<strong>Cancel</strong>
								</Button>
							</Grid>
						</Grid>
					</DialogActions>
				</Dialog>
			</Draggable>
		}
		{confirmOpen && (
			<ConfirmationDialogComponent
				open={confirmOpen}
				content={`Are you sure you want to exit without diagnosis?`}
				handleConfirmClose={handleConfirmClose}
				confirmStatus={confirmStatus}
			/>
		)}
	</>
}

export default withStyles(stylesheet)(ConditionEvaluationDialogComponent)